import { useQueryClient } from '@tanstack/react-query';
import {
  ApprovalItemModel,
  ApprovalVerdictItemModel,
  ApprovalVerdictStatusEnum,
  getApprovalItemKey,
  getApprovalItemsKey,
  useUpdateApprovalItem,
} from 'api-hooks/approval-item';
import { useGetMe } from 'api-hooks/auth/query';
import { RoleLiteModel } from 'api-hooks/role';
import { CheckCircleSVG, XCircleSVG } from 'common/assets';
import { AuthorizationRules } from 'common/constants';
// import { queryClient } from 'common/repositories/query-client';
import notification from 'components/common/notification';
import Separator from 'components/common/separator';
import { Button } from 'components/elements/buttons';
import { useAuthorization } from 'hooks/use-authorization';
import useDialog from 'hooks/use-dialog';
import { FullContainer } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';

interface Props {
  approval: ApprovalItemModel;
}

function approvalVerdict(props: {
  approvalVerdicts: ApprovalVerdictItemModel[];
  employeeRoles: RoleLiteModel[];
}) {
  const { approvalVerdicts: _approvalVerdicts, employeeRoles } = props;
  const approvalVerdicts = _approvalVerdicts
    .filter((curr) => curr.status === ApprovalVerdictStatusEnum.Pending)
    .sort((prev, curr) => {
      if (prev.order < curr.order) {
        return -1;
      }
      if (prev.order > curr.order) {
        return 1;
      }
      return 0;
    })
    .find(
      (curr) =>
        !!employeeRoles.find(
          (employeeCurr) => employeeCurr.id === curr.role.id,
        ),
    );

  return {
    id: approvalVerdicts?.id,
    hasAction:
      !_approvalVerdicts.find(
        (curr) => curr.status === ApprovalVerdictStatusEnum.Rejected,
      ) && approvalVerdicts,
  };
}

export default function ActionComponent(props: Props) {
  const { t } = useTranslation();
  const dialog = useDialog();
  const { mutateAsync, isLoading } = useUpdateApprovalItem();
  const { can } = useAuthorization();
  const { data: employeeData } = useGetMe();
  const employee = employeeData?.data;
  const queryClient = useQueryClient();

  const approvalId = approvalVerdict({
    approvalVerdicts: props.approval.approvalVerdicts,
    employeeRoles: employee?.roles || [],
  });

  const onAction = (action: 'approve' | 'reject') => {
    dialog.showConfirmation({
      title: t('common:confirmation'),
      message: t('common:confirmation_text'),
      onPositiveAction: async (dismiss) => {
        try {
          dismiss();
          const result = await mutateAsync({
            approvalId: approvalId.id!,
            body: { action },
          });
          queryClient.refetchQueries(
            getApprovalItemKey({ id: props.approval.id }),
          );
          queryClient.refetchQueries([getApprovalItemsKey()[0]]);
          notification.success({ message: result.message });
        } catch (error) {
          notification.error({ message: error.message });
        }
      },
      onNegativeAction: (dismiss) => dismiss(),
    });
  };

  if (!approvalId.hasAction) {
    return null;
  }

  if (!can(AuthorizationRules.ApprovalItemsUpdateApprovalVerdict)) {
    return null;
  }

  return (
    <FullContainer style={{ display: 'flex' }}>
      <Button
        buttonType="secondary"
        onClick={() => onAction('reject')}
        startEnhancer={(color, size) => (
          <XCircleSVG color={color} size={size} />
        )}
        disabled={isLoading}
      >
        {t('common:reject')}
      </Button>
      <Separator gap={16} />
      <Button
        onClick={() => onAction('approve')}
        startEnhancer={(color, size) => (
          <CheckCircleSVG color={color} size={size} />
        )}
        disabled={isLoading}
      >
        {t('common:approve')}
      </Button>
    </FullContainer>
  );
}
